/**
 * The below lint rules are disabled because Box relies on building styles at runtime in Forge apps.
 * This implementation is copied from @atlaskit/primitives/src/components/box.tsx which has the same
 * pattern. The css prop with runtime-computed values is required for the xcss prop to work correctly.
 * See go/DSP-18766 for more context.
 *
 * @atlaskit/design-system/consistent-css-prop-usage is disabled because this component must import
 * shared style maps (backgroundColorStylesMap, surfaceColorStylesMap, paddingStylesMap) for runtime
 * token resolution. Inlining these maps would create unmaintainable code duplication across Box and
 * Pressable components. This is an architectural requirement for Forge UI Kit's token system.
 */
/* eslint-disable @atlaskit/design-system/consistent-css-prop-usage -- Required for runtime xcss resolution with imported token style maps. See explanation above and go/DSP-18766 */
/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values -- Required for token map imports. See go/DSP-18766 */
/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-values -- Required for runtime style computation. See go/DSP-18766 */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// @ts-ignore - jsx is used by pragma
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled, @typescript-eslint/no-unused-vars -- jsx is used by pragma
import { css, jsx } from '@emotion/react';
import type { CSSObject, SerializedStyles } from '@emotion/serialize';
import React, { createContext } from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import { xcssValidator } from '../../utils/xcssValidator';
import type { XCSSProp } from '../../utils/xcssValidator';
import type { BackgroundColor, Space } from '../tokens.partial';
import {
	backgroundColorStylesMap,
	surfaceColorStylesMap,
	paddingStylesMap,
	isSurfaceColorToken,
	resolveTokens,
} from '../tokens.partial';

type NativeBoxProps = {
	backgroundColor?: BackgroundColor;
	children?: React.ReactNode;
	padding?: Space;
	paddingBlock?: Space;
	paddingBlockEnd?: Space;
	paddingBlockStart?: Space;
	paddingInline?: Space;
	paddingInlineEnd?: Space;
	paddingInlineStart?: Space;
	role?: string;
	testId?: string;
	xcss?: XCSSProp;
};

/**
 * Generates SerializedStyles from xcss style object.
 * We use `increaseSpecificity` to make sure this Emotion beats out Compiled.
 */
function generateXcss(styleObj: CSSObject, increaseSpecificity = true): SerializedStyles {
	const specificity = increaseSpecificity ? '&&' : ('&' as const);
	const resolved = resolveTokens(styleObj);
	return css({ [specificity]: resolved });
}

// Base styles for the Box component
const baseStyles = css({
	boxSizing: 'border-box',
	appearance: 'none',
	border: 'none',
});

const SurfaceContext = createContext<BackgroundColor>('elevation.surface');
SurfaceContext.displayName = 'SurfaceProvider';

// Internal Box component implementation (migrated from @atlaskit/primitives)
const InternalBox = ({
	children,
	backgroundColor,
	padding,
	paddingBlock,
	paddingBlockStart,
	paddingBlockEnd,
	paddingInline,
	paddingInlineStart,
	paddingInlineEnd,
	testId,
	xcss: xcssValue,
	role,
}: NativeBoxProps) => {
	// Build styles array from SerializedStyles
	const styles = [
		baseStyles,
		backgroundColor && backgroundColorStylesMap[backgroundColor],
		isSurfaceColorToken(backgroundColor) && surfaceColorStylesMap[backgroundColor],
		padding && paddingStylesMap.padding[padding],
		paddingBlock && paddingStylesMap.paddingBlock[paddingBlock],
		paddingBlockStart && paddingStylesMap.paddingBlockStart[paddingBlockStart],
		paddingBlockEnd && paddingStylesMap.paddingBlockEnd[paddingBlockEnd],
		paddingInline && paddingStylesMap.paddingInline[paddingInline],
		paddingInlineStart && paddingStylesMap.paddingInlineStart[paddingInlineStart],
		paddingInlineEnd && paddingStylesMap.paddingInlineEnd[paddingInlineEnd],
		xcssValue && generateXcss(xcssValue as CSSObject),
	].filter(Boolean);

	const node = (
		<div css={styles} data-testid={testId} role={role}>
			{children}
		</div>
	);

	return backgroundColor ? (
		<SurfaceContext.Provider value={backgroundColor}>{node}</SurfaceContext.Provider>
	) : (
		node
	);
};

/**
 * A box is a generic container that provides managed access to design tokens.
 */
export const Box = (props: Parameters<RenderFn>[0]): React.JSX.Element => {
	const {
		padding,
		paddingBlock,
		paddingBlockStart,
		paddingBlockEnd,
		paddingInline,
		paddingInlineStart,
		paddingInlineEnd,
		backgroundColor,
		testId,
		xcss,
		role,
	} = props.forgeDoc.props as NativeBoxProps;
	return (
		<InternalBox
			padding={padding}
			paddingBlock={paddingBlock}
			paddingBlockStart={paddingBlockStart}
			paddingBlockEnd={paddingBlockEnd}
			paddingInline={paddingInline}
			paddingInlineStart={paddingInlineStart}
			paddingInlineEnd={paddingInlineEnd}
			backgroundColor={backgroundColor}
			testId={testId}
			xcss={xcss && xcssValidator(xcss)}
			role={role}
		>
			{props.forgeDoc.children.map((c) => (
				<React.Fragment key={c.key}>{props.render(c)}</React.Fragment>
			))}
		</InternalBox>
	);
};
